import {
  ContactStatusOptionsObject,
  FollowedSecurityPolicyOptionsObject,
  enumEmpty,
} from '../../api/CoachingForm/schema';
import {
  ConsolidatedContact,
  ContactDetailsInput,
  ContactViewTabObject,
} from '../../api/CoachingNotes/schema';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import { FormDetailsInput } from './backendFormData.types';
import {
  Section,
  Question,
  QuestionOption,
  Extra,
  FormData,
  DropdownItem,
} from './formData.types';
import { FormSaveType } from './formData.constants';
import { convertToVerticalOption, D2_VERTICALS } from '../Vertical';
import { FORM_DISPLAY_NAMES, FORM_IDS } from './formSectionConstants';

// Helper function to get a section by its ID
const grabSectionByID = (
  dataset: FormData,
  sectionID: string
): Section | undefined => {
  return dataset.SECTIONS?.find(section => section.SECTION_ID === sectionID);
};

// Helper function to get a question by its ID within a specific section
const grabQuestionByID = (
  section: Section,
  questionID: string
): Question | undefined => {
  return section.QUESTIONS.find(
    question => question.QUESTION_ID === questionID
  );
};

// Helper function to get all options for a dropdown question by its ID within a specific section
const grabDropdownOptionsByID = (
  section: Section,
  questionID: string
): QuestionOption[] => {
  const question = grabQuestionByID(section, questionID);
  return question ? question.QUESTION_OPTIONS || [] : [];
};

// Helper function to get all options for a radio button section question by its ID within a specific section
const grabRadioOptionsByID = (
  section: Section,
  questionID: string
): QuestionOption[] => {
  const question = grabQuestionByID(section, questionID);
  return question ? question.QUESTION_OPTIONS || [] : [];
};

// Helper function to get bullet points for a bullet point question by its ID within a specific section
const grabBulletPointsByID = (
  section: Section,
  questionID: string
): QuestionOption[] => {
  const question = grabQuestionByID(section, questionID);
  return question ? question.QUESTION_OPTIONS || [] : [];
};

// Helper function to get an extra field by its translation ID
const grabExtraFieldByID = (
  dataset: FormData | undefined,
  translationID: string
): Extra | undefined => {
  return dataset?.EXTRA
    ? dataset?.EXTRA?.find(extra => extra.TRANSLATION_ID === translationID)
    : void 0;
};

// Helper function to get the default message by translation ID
const getDefaultMessageByID = (
  dataset: FormData | undefined,
  translationID: string
): string => {
  const extra = grabExtraFieldByID(dataset, translationID);
  return extra ? extra.DEFAULT_MESSAGE : '';
};

// This function will merge feedback details with a contacts contactInfo
// It will prioritize feedbackDetails, meaning if anything is defined in both places, feedback details will be chosen
const mergeFeedbackIntoContactDetails = (
  feedbackDetails: ContactDetailsInput | undefined,
  contact: ConsolidatedContact,
  vertical?: any,
  formInput?: FormDetailsInput,
  ingress?: string
): ContactDetailsInput => {
  let ingressValue = ingress ?? '';
  let feedbackDetailsCopy: ContactDetailsInput = {
    ...feedbackDetails,
  };
  let saveOrigin: number = 0;
  let contactTab;
  if (contact!.recommended !== 0) {
    saveOrigin = 1;
    contactTab = ContactViewTabObject.RECOMMENDED;
    ingressValue = ingressValue ?? ContactViewTabObject.RECOMMENDED;
  } else if (contact.recent !== 0) {
    saveOrigin = 2;
    contactTab = ContactViewTabObject.RECENT;
    ingressValue = ingressValue ?? ContactViewTabObject.RECENT;
  } else if (
    contact.saved !== 0 &&
    (contact.contactStatus === ContactStatusOptionsObject.DRAFT ||
      contact.contactStatus === null)
  ) {
    saveOrigin = (contact as ConsolidatedContact).saveOrigin;
    contactTab = ContactViewTabObject.SAVED;
    ingressValue = ingressValue ?? ContactViewTabObject.SAVED;
  }

  if (!feedbackDetailsCopy?.ingress) {
    feedbackDetailsCopy = {
      ...feedbackDetailsCopy,
      ingress: ingressValue,
    };
  }

  let followedSecurityPolicies;
  let relatedMetric;
  if (formInput) {
    const securityQuestion = formInput.formAnswers.find(
      ans =>
        ans.questionID ===
          'TX_MONITORING_DID_ASSOCIATE_FOLLOW_SECURITY_POLICIES' ||
        ans.questionID ===
          'D2_MONITORING_DID_ASSOCIATE_FOLLOW_SECURITY_POLICIES'
    );
    const securityPolicy = securityQuestion?.selectedOption?.defaultMessage;
    followedSecurityPolicies =
      securityPolicy === 'Yes'
        ? FollowedSecurityPolicyOptionsObject.YES
        : securityPolicy === 'No'
          ? FollowedSecurityPolicyOptionsObject.NO
          : FollowedSecurityPolicyOptionsObject.NA;

    const metric = formInput.formAnswers.find(ans =>
      ans.questionID.includes('METRIC')
    );
    relatedMetric = metric?.selectedOption?.defaultMessage;
  }

  const input: ContactDetailsInput = {
    goodRecommendation: contact?.contactInfo?.goodRecommendation ?? enumEmpty,
    saveOrigin: saveOrigin,
    recommended: contact?.recommended,
    contactTab: contactTab,
    vertical,
    followedSecurityPolicies: followedSecurityPolicies ?? enumEmpty,
    saved: 1,
    relatedMetric: relatedMetric ?? enumEmpty,
    contactDate: contact?.contactDate,
    ...feedbackDetailsCopy,
  };
  return input;
};

// Helper function to get the checked value's string format. This method is used by the SDS forms.
const getCheckedValueSelectionAsString = (
  checkedValue: number | undefined
): string => {
  return checkedValue === 0
    ? 'Expert'
    : checkedValue === 1
      ? 'Intermediate'
      : checkedValue === 2
        ? 'Novice'
        : '';
};

// Helper function to get the checked value's string format. This method is used by the SDS forms.
const getCheckedValueSelectionAsStringDartForm = (
  checkedValue: number | undefined
): string => {
  return checkedValue === 0
    ? 'Meets bar'
    : checkedValue === 1
      ? 'Doesnt meet bar'
      : checkedValue === 2
        ? 'Not Applicable'
        : '';
};

// Helper function to get the checked value's string format as a snake_case ID.
// Reuses getCheckedValueSelectionAsStringDartForm and converts to ID format.
// Example: "Meets bar" → "meets_bar", "Doesnt meet bar" → "doesnt_meet_bar"
const getCheckedValueSelectionAsStringDartFormId = (
  checkedValue: number | undefined
): string => {
  const displayValue = getCheckedValueSelectionAsStringDartForm(checkedValue);
  return displayValue ? displayValue.toLowerCase().replace(/\s+/g, '_') : '';
};

// Helper function to scroll to the section containing the stepper columns once the
// next button is clicked.
const scrollToStepperColumns = (
  showStepperColumns: boolean,
  stepperColumnsSpanId: string
) => {
  if (showStepperColumns) {
    const element = document.querySelector(`#${stepperColumnsSpanId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // in case element hasn't been added to the DOM yet
      const observer = new MutationObserver(() => {
        const element = document.querySelector(`#${stepperColumnsSpanId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          observer.disconnect(); // Stop observing after a successful scroll
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      return () => {
        observer.disconnect();
      };
    }
  }
};

const isFormReviewedOrDelivered = (
  contactId: string | undefined,
  contactStatus: string | undefined
) => {
  return contactId
    ? contactStatus === FormSaveType.REVIEWED ||
        contactStatus === FormSaveType.DELIVERED
    : false;
};

const isD2Option = (option: QuestionOption): boolean => {
  const message = option.DEFAULT_MESSAGE?.toLowerCase() || '';
  const translationId = option.TRANSLATION_ID?.toLowerCase() || '';

  return message.includes('d2') || translationId.includes('d2_form');
};

const filterOptionsByVerticalType = (
  selectedVertical: string,
  options: QuestionOption[]
): QuestionOption[] => {
  const verticalOption = convertToVerticalOption(selectedVertical);
  const isD2Vertical = verticalOption && D2_VERTICALS.has(verticalOption);

  return isD2Vertical
    ? options.filter(option => isD2Option(option))
    : options.filter(option => !isD2Option(option));
};

const getFormIdForDraftLookup = (formType: string): string => {
  if (formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW) {
    return FORM_IDS.D2_FORM;
  }
  return FORM_IDS.TRANSACTION_MONITORING_FORM;
};

export {
  grabSectionByID,
  grabQuestionByID,
  grabDropdownOptionsByID,
  grabRadioOptionsByID,
  grabBulletPointsByID,
  grabExtraFieldByID,
  getDefaultMessageByID,
  mergeFeedbackIntoContactDetails,
  getCheckedValueSelectionAsString,
  getCheckedValueSelectionAsStringDartForm,
  getCheckedValueSelectionAsStringDartFormId,
  scrollToStepperColumns,
  isFormReviewedOrDelivered,
  isD2Option,
  filterOptionsByVerticalType,
  getFormIdForDraftLookup,
};
